import appClient, { ResultApi } from '../../common/services/AppClient';
import { CancelToken } from 'axios';
import {
    DataCrafterTemplate,
    CreateDataCrafterTemplateRequest,
    UpdateDataCrafterTemplateRequest,
    ChangeDataCrafterTemplateStatusRequest,
    PackageColumnsResponse,
    DataCrafterTemplateColumn,
    DataCrafterTemplateCommitDataModel,
    CodeValidationResponse,
    ScriptGenerationResponse,
    TestPickerScriptResponse,
    ExcelScriptRunJobResponse
} from '../types';
import security from '../../common/services/SecurityService';
import { Utils } from '../../common/services/Utils';
import DataCrafterTemplateImportResponse from '../types/responses/DataCrafterTemplateImportResponse';
import { endpoints } from '../../../config';

interface FullProgressPayload {
    step: number;
    total_steps: number;
    message: string;
    status: string;
    duration: number | null;
    step_details?: {
        model_output?: string;
        observations?: string;
        action_output?: string | null;
        tool_calls?: Array<unknown>;
    } | null;
}

interface FullDeltaPayload {
    type: 'chat_completion_delta';
    is_streaming: boolean;
    content?: string;
}

interface FullActionStepPayload {
    type: 'action_step';
    content: string;
    role: string;
    is_streaming: boolean;
    duration: number | null;
    current_step: number;
    total_steps: number;
}

interface FullCompletionPayload {
    scriptRefId: string;
    code: string;
}

interface FullErrorPayload {
    error: string;
}

type FullStreamPayload =
    | FullProgressPayload
    | FullDeltaPayload
    | FullActionStepPayload
    | FullCompletionPayload
    | FullErrorPayload;

interface IncrementalCompletionPayload {
    status: 'success';
    scriptRefId: string;
    code: string;
    compilationMode: IncrementalCompilationMode;
    savedCodeCurrent?: boolean;
    attempts: number;
    cacheDecision?: string | null;
    fallbackReason?: string | null;
    generationSeconds: number;
    verificationSeconds: number;
    fullRebuildSeconds: number;
    totalSeconds: number;
}

interface IncrementalErrorPayload {
    status: 'error';
    error: string;
    code: string;
}

type IncrementalStreamPayload = IncrementalCompletionPayload | IncrementalErrorPayload;

export type IncrementalCompilationMode = 'incremental' | 'no_change' | 'full_bootstrap' | 'full_rebuild';

export interface IncrementalCompilationResult {
    scriptRefId: string;
    code: string;
    compilationMode: IncrementalCompilationMode;
    /** Whether the returned code is the template's saved code; omitted by older services. */
    savedCodeCurrent?: boolean;
    attempts: number;
    cacheDecision?: string;
    fallbackReason?: string;
    generationSeconds: number;
    verificationSeconds: number;
    fullRebuildSeconds: number;
    totalSeconds: number;
}

export interface TemplateScriptCompilationResult {
    scriptRefId: string;
    code: string;
}

interface CompileStreamOptions {
    onProgress?: (progress: FullProgressPayload) => void;
    onStreaming?: (delta: string) => void;
}

function isIncrementalCompletion(payload: IncrementalStreamPayload): payload is IncrementalCompletionPayload {
    if (payload?.status !== 'success') {
        return false;
    }

    const hasScript = typeof payload.scriptRefId === 'string' && typeof payload.code === 'string';
    const hasMode = ['incremental', 'no_change', 'full_bootstrap', 'full_rebuild'].includes(payload.compilationMode);
    const hasAttemptCount = Number.isInteger(payload.attempts) && payload.attempts >= 0;
    const hasTimings = [
        payload.generationSeconds,
        payload.verificationSeconds,
        payload.fullRebuildSeconds,
        payload.totalSeconds
    ].every(isNonNegativeNumber);

    const hasReasons = [payload.cacheDecision, payload.fallbackReason].every(isOptionalString);
    const hasSavedFlag = payload.savedCodeCurrent === undefined || typeof payload.savedCodeCurrent === 'boolean';

    return hasScript && hasMode && hasAttemptCount && hasTimings && hasReasons && hasSavedFlag;
}

function isNonNegativeNumber(value: unknown): boolean {
    return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

function isOptionalString(value: unknown): boolean {
    return value == null || typeof value === 'string';
}

export default class DataCrafterTemplatesService {
    private static async extractErrorBody(fetchResponse: Response): Promise<string> {
        try {
            const text = await fetchResponse.text();
            const json = JSON.parse(text);
            return json?.detail || text;
        } catch {
            return 'Unknown error';
        }
    }

    // Create a new output file template
    async createTemplate(
        projectId: string,
        data: CreateDataCrafterTemplateRequest
    ): Promise<DataCrafterTemplate | null> {
        try {
            const response = await appClient.post<DataCrafterTemplate | null>(
                endpoints.manageUrl + `projects/${projectId}/data-crafter-templates`,
                data
            );
            return response.unwrapOr(null);
        } catch (error) {
            console.error('Error creating template:', error);
            throw error;
        }
    }

    // Retrieve a list of output file templates
    async getTemplates(projectId: string): Promise<DataCrafterTemplate[]> {
        try {
            const response = await appClient.get<DataCrafterTemplate[]>(
                endpoints.manageUrl + `projects/${projectId}/data-crafter-templates`
            );
            return response.unwrapOr([]);
        } catch (error) {
            console.error('Error fetching templates:', error);
            throw error;
        }
    }

    // Retrieve a single output file template by ID
    async getTemplateById(templateId: string, projectId: string): Promise<unknown> {
        try {
            const response = await appClient.get<unknown>(
                endpoints.manageUrl + `projects/${projectId}/data-crafter-templates/${templateId}`
            );
            return response.unwrapOr(null);
        } catch (error) {
            console.error('Error fetching template:', error);
            throw error;
        }
    }

    // Update an existing output file template
    async updateTemplate(
        projectId: string,
        templateId: string,
        data: UpdateDataCrafterTemplateRequest
    ): Promise<DataCrafterTemplate | null> {
        try {
            const response = await appClient.update<DataCrafterTemplate | null>(
                endpoints.manageUrl + `projects/${projectId}/data-crafter-templates/${templateId}`,
                data
            );
            return response.unwrapOr(null);
        } catch (error) {
            console.error('Error updating template:', error);
            throw error;
        }
    }

    // Change the status of an output file template
    async changeTemplateStatus(
        projectId: string,
        templateId: string,
        data: ChangeDataCrafterTemplateStatusRequest
    ): Promise<DataCrafterTemplate | null> {
        try {
            const response = await appClient.update<DataCrafterTemplate | null>(
                endpoints.manageUrl + `projects/${projectId}/data-crafter-templates/${templateId}/status`,
                data
            );

            if (response.isErr()) {
                throw new Error(response.error.data?.title || response.error.text);
            }

            return response.unwrapOr(null);
        } catch (error) {
            console.error('Error changing template status:', error);
            throw error;
        }
    }

    // Delete an output file template
    async deleteTemplate(projectId: string, templateId: string): Promise<boolean> {
        try {
            const response = await appClient.delete<boolean>(
                endpoints.manageUrl + `projects/${projectId}/data-crafter-templates/${templateId}`
            );
            return response.unwrapOr(false);
        } catch (error) {
            console.error('Error deleting template:', error);
            throw error;
        }
    }

    async fetchPackagesColumns(packageIds: string[], cancelToken?: CancelToken): Promise<PackageColumnsResponse> {
        const url = endpoints.datacrafterUrl + 'excel';
        const response = await appClient.post<PackageColumnsResponse>(
            url,
            {
                packageIds
            },
            cancelToken
        );

        return response.unwrapOr({ columns: [] });
    }

    async duplicateTemplate(
        projectId: string,
        templateId: string,
        newName: string
    ): Promise<DataCrafterTemplate | null> {
        try {
            const response = await appClient.post<DataCrafterTemplate | null>(
                endpoints.manageUrl + `projects/${projectId}/data-crafter-templates/${templateId}/duplicate`,
                { newName }
            );
            return response.unwrapOr(null);
        } catch (error) {
            console.error('Error duplicating template:', error);
            throw error;
        }
    }

    async createTemplateScriptRunJob(
        projectId: string,
        templateId: string | null,
        inputPackageIds: string[],
        outputPackageId: string,
        templateScriptRefId: string | null,
        outputVariants: unknown[],
        variantPickerScriptRefId: string | null,
        selectedVariantId?: string
    ): Promise<ExcelScriptRunJobResponse> {
        const isPreview = !templateId || templateId.startsWith('temp-template-') || Boolean(templateScriptRefId);
        const url = isPreview
            ? endpoints.datacrafterUrl + 'excel/data-crafter-template-previews/run/job'
            : endpoints.datacrafterUrl + `excel/data-crafter-templates/${templateId}/run/job`;

        const response = await appClient.post<ExcelScriptRunJobResponse>(
            url,
            isPreview
                ? {
                      projectId,
                      inputPackageIds,
                      outputPackageId,
                      templateScriptRefId,
                      outputVariants,
                      variantPickerScriptRefId,
                      selectedVariantId
                  }
                : {
                      inputPackageIds,
                      templateScriptRefId,
                      outputVariants,
                      variantPickerScriptRefId,
                      selectedVariantId
                  }
        );

        return response.match(
            value => value,
            error => {
                const message = error.data?.title ?? error.text ?? 'Failed to start template script run job';
                throw new Error(message);
            }
        );
    }

    async getTemplateScriptRunJob(jobId: string): Promise<ExcelScriptRunJobResponse> {
        const response = await appClient.get<ExcelScriptRunJobResponse>(
            endpoints.datacrafterUrl + `excel/data-crafter-template-runs/${jobId}`
        );

        return response.match(
            value => value,
            error => {
                const message = error.data?.title ?? error.text ?? 'Failed to fetch template script run job status';
                throw new Error(message);
            }
        );
    }

    async downloadTemplateScriptRunJobResult(jobId: string): Promise<Blob> {
        try {
            const response = await security.invoke(async token => {
                const fetchResponse = await fetch(
                    endpoints.datacrafterUrl + `excel/data-crafter-template-runs/${jobId}/result`,
                    {
                        method: 'GET',
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    }
                );

                if (!fetchResponse.ok) {
                    throw new Error(await DataCrafterTemplatesService.extractErrorBody(fetchResponse));
                }

                return await fetchResponse.blob();
            });

            return response;
        } catch (error) {
            console.error('Error downloading template script run job result:', error);
            throw error;
        }
    }

    async validateTemplateScript(
        inputPackageIds: string[],
        outputPackageId: string,
        code: string,
        outputColumns: DataCrafterTemplateColumn[],
        validatorModelId: string
    ): Promise<CodeValidationResponse> {
        try {
            const response = await security.invoke(async token => {
                const fetchResponse = await fetch(endpoints.datacrafterUrl + 'excel/script/validate', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        inputPackageIds,
                        outputPackageId,
                        code,
                        columns: outputColumns,
                        validatorModelId
                    })
                });

                if (!fetchResponse.ok) {
                    throw new Error(await DataCrafterTemplatesService.extractErrorBody(fetchResponse));
                }

                return await fetchResponse.json();
            });

            return response;
        } catch (error) {
            console.error('Error validating template script:', error);
            throw error;
        }
    }

    async compileTemplateScriptStream(
        inputPackageIds: string[],
        outputPackageId: string,
        outputColumns: DataCrafterTemplateColumn[],
        coderModelId: string | null,
        projectId: string,
        templateId: string | null,
        options: CompileStreamOptions,
        outputVariantId?: string
    ): Promise<TemplateScriptCompilationResult> {
        const url = endpoints.datacrafterUrl + 'excel/script/compile/stream';

        const requestData = {
            inputPackageIds,
            outputPackageId,
            columns: outputColumns,
            coderModelId,
            projectId,
            templateId,
            outputVariantId
        };

        let terminal: { result: TemplateScriptCompilationResult } | { error: Error } | undefined;

        const setTerminal = (next: { result: TemplateScriptCompilationResult } | { error: Error }): void => {
            if (!terminal) {
                terminal = next;
            }
        };

        await appClient.createStreamingRequest<FullStreamPayload>(url, requestData, {
            onMessage: (data, eventName) => {
                if (terminal) {
                    return;
                }

                switch (eventName) {
                    case 'complete': {
                        const completion = data as FullCompletionPayload;

                        if (typeof completion?.scriptRefId !== 'string' || typeof completion?.code !== 'string') {
                            setTerminal({ error: new Error('Invalid template compilation stream message') });
                            return;
                        }

                        setTerminal({ result: completion });
                        return;
                    }
                    case 'error': {
                        const error = data as FullErrorPayload;

                        if (typeof error?.error !== 'string') {
                            setTerminal({ error: new Error('Invalid template compilation stream message') });
                            return;
                        }

                        setTerminal({ error: new Error(error.error) });
                        return;
                    }
                    case 'progress':
                        options.onProgress?.(data as FullProgressPayload);
                        return;
                    case 'chat_completion_delta':
                        options.onStreaming?.((data as FullDeltaPayload).content || '');
                        return;
                    case 'action_step':
                        return;
                    default:
                        setTerminal({ error: new Error('Invalid template compilation stream message') });
                }
            },
            onError: error => setTerminal({ error })
        });

        if (!terminal) {
            throw new Error('Template compilation stream ended without a terminal event');
        }

        if ('error' in terminal) {
            throw terminal.error;
        }

        return terminal.result;
    }

    async compileTemplateScriptIncrementallyStream(
        projectId: string,
        templateId: string,
        coderModelId: string | null,
        outputVariantId?: string,
        forceFull: boolean = false
    ): Promise<IncrementalCompilationResult> {
        const url = endpoints.datacrafterUrl + 'excel/script/compile/incremental/stream';

        let terminal: { result: IncrementalCompilationResult } | { error: Error } | undefined;

        const setTerminal = (next: { result: IncrementalCompilationResult } | { error: Error }): void => {
            if (!terminal) {
                terminal = next;
            }
        };

        await appClient.createStreamingRequest<IncrementalStreamPayload>(
            url,
            {
                projectId,
                templateId,
                outputVariantId,
                coderModelId,
                forceFull
            },
            {
                onMessage: (data, eventName) => {
                    if (terminal) {
                        return;
                    }

                    switch (eventName) {
                        case 'complete': {
                            if (!isIncrementalCompletion(data)) {
                                setTerminal({ error: new Error('Invalid incremental compilation stream message') });
                                return;
                            }

                            setTerminal({
                                result: {
                                    scriptRefId: data.scriptRefId,
                                    code: data.code,
                                    compilationMode: data.compilationMode,
                                    savedCodeCurrent: data.savedCodeCurrent,
                                    attempts: data.attempts,
                                    cacheDecision: data.cacheDecision ?? undefined,
                                    fallbackReason: data.fallbackReason ?? undefined,
                                    generationSeconds: data.generationSeconds,
                                    verificationSeconds: data.verificationSeconds,
                                    fullRebuildSeconds: data.fullRebuildSeconds,
                                    totalSeconds: data.totalSeconds
                                }
                            });
                            return;
                        }
                        case 'error': {
                            const error = data as IncrementalErrorPayload;

                            if (error?.status !== 'error' || typeof error?.error !== 'string') {
                                setTerminal({ error: new Error('Invalid incremental compilation stream message') });
                                return;
                            }

                            setTerminal({ error: new Error(error.error) });
                            return;
                        }
                        default:
                            setTerminal({ error: new Error('Invalid incremental compilation stream message') });
                    }
                },
                onError: error => setTerminal({ error })
            }
        );

        if (!terminal) {
            throw new Error('Incremental compilation stream ended without a terminal event');
        }

        if ('error' in terminal) {
            throw terminal.error;
        }

        return terminal.result;
    }

    async compileTemplateVariantPickerScript(
        outputVariants: unknown[],
        variantPickerModelId: string,
        projectId: string,
        templateId?: string
    ): Promise<ScriptGenerationResponse> {
        try {
            const requestData = {
                outputVariants,
                variantPickerModelId,
                projectId,
                templateId
            };
            const response = await security.invoke(async token => {
                const fetchResponse = await fetch(endpoints.datacrafterUrl + 'excel/script/compile/variant-picker', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify(requestData)
                });

                if (!fetchResponse.ok) {
                    throw new Error('Network response was not ok');
                }

                return await fetchResponse.json();
            });

            return response;
        } catch (error) {
            console.error('Error validating template script:', error);
            throw error;
        }
    }

    async testTemplateVariantPickerScript(
        projectId: string,
        templateId: string | null,
        inputPackageIds: string[],
        variantPickerScriptRefId: string,
        outputVariants: unknown[]
    ): Promise<TestPickerScriptResponse> {
        try {
            const isPreview =
                !templateId || templateId.startsWith('temp-template-') || Boolean(variantPickerScriptRefId);
            const requestData = {
                projectId,
                inputPackageIds,
                variantPickerScriptRefId,
                outputVariants
            };
            const response = await security.invoke(async token => {
                const fetchResponse = await fetch(
                    isPreview
                        ? endpoints.datacrafterUrl + 'excel/data-crafter-template-previews/variant-picker/run'
                        : endpoints.datacrafterUrl + `excel/data-crafter-templates/${templateId}/variant-picker/run`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`
                        },
                        body: JSON.stringify(requestData)
                    }
                );

                if (!fetchResponse.ok) {
                    throw new Error('Network response was not ok');
                }

                return await fetchResponse.json();
            });

            return response;
        } catch (error) {
            console.error('Error validating template script:', error);
            throw error;
        }
    }

    async exportTemplate(
        templateId: string,
        projectId: string,
        includeModels: boolean = false,
        includeInputPackages: boolean = false,
        includeOutputPackage: boolean = false
    ) {
        const mapForm = document.createElement('form');
        mapForm.setAttribute('id', 'dataCrafterTemplatePostForm');
        mapForm.target = '_blank';
        mapForm.method = 'POST';
        const mapTemplateIdInput = document.createElement('input');
        mapTemplateIdInput.type = 'text';
        mapTemplateIdInput.name = 'id';
        mapTemplateIdInput.value = templateId;
        mapForm.appendChild(mapTemplateIdInput);

        await security.invoke(token => {
            let t =
                '?access_token=' +
                encodeURIComponent(token) +
                `&includeModels=${includeModels}` +
                `&includeInputPackages=${includeInputPackages}` +
                `&includeOutputPackage=${includeOutputPackage}`;
            const url = endpoints.manageUrl + `projects/${projectId}/data-crafter-templates/${templateId}/export${t}`;
            Utils.downloadFile(url, mapForm, 'dataCrafterTemplatePostForm');
            return Promise.resolve();
        });
    }

    async importTemplate(projectId: string, data: FormData): Promise<ResultApi<DataCrafterTemplateImportResponse>> {
        const url = `${endpoints.manageUrl}projects/${projectId}/data-crafter-templates/import`;
        return await appClient.post(url, data);
    }

    async commitImportedTemplate(projectId: string, importData: DataCrafterTemplateCommitDataModel) {
        const request = {
            ...importData
        };
        const url = `${endpoints.manageUrl}projects/${projectId}/data-crafter-templates/import/commit`;
        return await appClient.post(url, request);
    }
}
